import {
  type ComponentPropsWithRef,
  type Ref,
  type CSSProperties,
  Fragment,
  useState,
  useRef,
  useEffect,
} from "react";
import { useNavigate } from "@tanstack/react-router";
import { useCurrentOrg } from "@/services/permissions";
import { Dropdown } from "react-bootstrap";
import { UserOrg } from "@/models/user.tsx";
import { useLoginDetail } from "@/queries.ts";
import Icon from "@/components/shared/Icon";

const NAVY = "var(--color-p2)";
const SELECTED_BG = "#f8fafc";
const HOVER_BG = "#f1f5f9";

const headerTextStyle: CSSProperties = {
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: "18px",
  lineHeight: "20px",
  fontWeight: 500,
  color: "#0F172A",
};

const dropdownIconStyle: CSSProperties = {
  width: "18px",
  height: "18px",
  color: "#64748B",
};

const dropdownMenuStyle: CSSProperties = {
  width: "max-content",
  minWidth: "100%",
  maxWidth: "360px",
};

const searchHeaderStyle: CSSProperties = {
  padding: "8px 12px",
  borderBottom: "1px solid #E2E8F0",
  position: "sticky",
  top: 0,
  background: "white",
  zIndex: 1,
};

const searchInputStyle: CSSProperties = {
  width: "100%",
  padding: "6px 10px",
  border: "1px solid #CBD5E1",
  borderRadius: "6px",
  fontSize: "14px",
  outline: "none",
};

const searchInputFocusStyle: CSSProperties = {
  border: `1px solid ${NAVY}`,
  boxShadow: "0 0 0 3px color-mix(in srgb, var(--color-p2) 12%, transparent)",
};

const scrollAreaStyle: CSSProperties = {
  maxHeight: "min(560px, 70vh)",
  overflowY: "auto",
};

const emptyStateStyle: CSSProperties = {
  padding: "12px 16px",
  color: "#64748B",
  fontSize: "14px",
};

const STATE_LEVEL_NAME = "State Level";
const isStateLevel = (o: UserOrg) =>
  o.orgName.trim().toLowerCase() === STATE_LEVEL_NAME.toLowerCase();

export default function LeaPicker() {
  const loginDetail = useLoginDetail();
  const currentOrg = useCurrentOrg();
  const currentLea = currentOrg?.orgName || "Select Organization";
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentOrg === undefined && loginDetail?.userOrgs?.length === 1) {
      navigate({ to: `/${loginDetail.userOrgs[0].esiIdPath}/` });
    }
  }, [currentOrg, loginDetail?.userOrgs, navigate]);

  const orgs = loginDetail?.userOrgs ?? [];
  const stateLevel = orgs.find(isStateLevel);
  const sortedLeas = orgs
    .filter((o) => !isStateLevel(o))
    .sort((a, b) => a.orgName.localeCompare(b.orgName));

  const term = search.trim().toLowerCase();
  const stateLevelOrg =
    stateLevel && (!term || STATE_LEVEL_NAME.toLowerCase().includes(term))
      ? stateLevel
      : undefined;
  const filteredLeas = term
    ? sortedLeas.filter((o) => o.orgName.toLowerCase().includes(term))
    : sortedLeas;

  const items: UserOrg[] = stateLevelOrg
    ? [stateLevelOrg, ...filteredLeas]
    : filteredLeas;

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const el = scrollAreaRef.current?.querySelector<HTMLElement>(
      `[data-active-index="${activeIndex}"]`
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, isOpen]);

  const handleSelect = (esiIdPath: string) => {
    navigate({ to: "/$leaid", params: { leaid: esiIdPath } });
    setIsOpen(false);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        if (items.length === 0) return;
        e.preventDefault();
        e.stopPropagation();
        setActiveIndex((i) => Math.min(i + 1, items.length - 1));
        return;
      case "ArrowUp":
        if (items.length === 0) return;
        e.preventDefault();
        e.stopPropagation();
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      case "Home":
        if (items.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex(0);
        }
        return;
      case "End":
        if (items.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex(items.length - 1);
        }
        return;
      case "Enter": {
        const active = items[activeIndex];
        if (active) {
          e.preventDefault();
          e.stopPropagation();
          handleSelect(active.esiIdPath);
        }
        return;
      }
      case "Escape":
        if (search) {
          e.stopPropagation();
          setSearch("");
        } else {
          setIsOpen(false);
        }
        return;
    }
  };

  const hasResults = items.length > 0;

  return (
    <Dropdown
      className="header-dropdown header-dropdown-lea"
      drop="down-centered"
      show={isOpen}
      onToggle={(next) => {
        setIsOpen(next);
        if (next) {
          setSearch("");
          setActiveIndex(0);
        }
      }}
      autoClose="outside"
    >
      <Dropdown.Toggle as={CustomToggle}>{currentLea}</Dropdown.Toggle>
      <Dropdown.Menu
        className="header-dropdown-menu rounded-4 p-0"
        style={dropdownMenuStyle}
      >
        <div style={searchHeaderStyle} onClick={(e) => e.stopPropagation()}>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search organizations..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleSearchKeyDown}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={{
              ...searchInputStyle,
              ...(searchFocused ? searchInputFocusStyle : {}),
            }}
            aria-label="Search organizations"
            aria-activedescendant={
              hasResults ? `lea-option-${activeIndex}` : undefined
            }
            role="combobox"
            aria-expanded={isOpen}
            aria-controls="lea-listbox"
          />
        </div>

        <div
          style={scrollAreaStyle}
          ref={scrollAreaRef}
          id="lea-listbox"
          role="listbox"
        >
          {items.map((org, index) => {
            const isStateLevelItem = !!stateLevelOrg && index === 0;
            const showDividerAfter =
              isStateLevelItem && filteredLeas.length > 0;
            const isActive = index === activeIndex;
            const isSelected = org.esiIdPath === currentOrg?.esiIdPath;

            const itemStyle: CSSProperties = {
              borderLeft: isSelected
                ? `3px solid ${NAVY}`
                : "3px solid transparent",
              backgroundColor: isActive
                ? HOVER_BG
                : isSelected
                  ? SELECTED_BG
                  : undefined,
              color: isSelected ? NAVY : "#0F172A",
              fontWeight: isSelected ? 600 : undefined,
            };

            return (
              <Fragment key={org.esiIdPath}>
                <Dropdown.Item
                  id={`lea-option-${index}`}
                  data-active-index={index}
                  role="option"
                  aria-selected={isSelected}
                  className={`dropdown-item header-dropdown-item py-2${
                    isStateLevelItem ? " fw-semibold" : ""
                  }`}
                  style={itemStyle}
                  onClick={() => handleSelect(org.esiIdPath)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  {org.orgName}
                </Dropdown.Item>
                {showDividerAfter && <Dropdown.Divider className="my-1" />}
              </Fragment>
            );
          })}

          {!hasResults && <div style={emptyStateStyle}>No matches</div>}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

function CustomToggle({
  children,
  onClick,
  className,
  ref,
  ...rest
}: ComponentPropsWithRef<"button"> & { ref?: Ref<HTMLButtonElement> }) {
  return (
    <button
      className="lea-dropdown py-2 px-1"
      style={headerTextStyle}
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick?.(e as any);
      }}
      {...rest}
    >
      <span className="mb-0 d-inline-flex align-items-center">
        {children}
        <span
          className="ms-1 d-inline-flex align-items-center justify-content-center"
          style={dropdownIconStyle}
        >
          <Icon name="chevron-down" />
        </span>
      </span>
    </button>
  );
}
